import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { LifeBuoy, Plus, ShieldAlert } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { EmptyState } from "@/components/EmptyState";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SupportAdminApiProvider } from "../admin/admin-api.tsx";
import { Backoffice } from "../admin/Backoffice.tsx";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import type { BackofficeSection, SettingsGroup } from "../router.tsx";
import { CONSOLE_SUPPORT_PATH, Link, supportGrantPath } from "../router.tsx";
import { SessionProvider } from "../session.tsx";
import { watchQueryClient } from "../shell/build-refresh.ts";
import { useCsrfHeaders, useOperator } from "./operator.tsx";
import { consoleRefusal, GrantStatus } from "./parts.tsx";
import { consoleKeys, grantLive, useOwnGrant, useOwnGrants, useTenants } from "./queries.ts";

const MAX_HOURS = 72;

/**
 * Support access (spec `support-access`): the operator's own requests and grants. A request
 * grants nothing until one of the tenant's admins approves it; an active grant opens that
 * tenant's backoffice read-only, every request audited in the tenant's log.
 */
export function SupportPage() {
  const { t } = useTranslation();
  const grants = useOwnGrants();
  const [requesting, setRequesting] = useState(false);
  const list = grants.data ?? [];
  return (
    <Page width="wide">
      <PageHeader
        title={t("console.support.title")}
        lead={t("console.support.lead")}
        actions={
          <Button onClick={() => setRequesting(true)}>
            <Plus aria-hidden="true" />
            {t("console.support.request")}
          </Button>
        }
      />
      {grants.isError ? (
        <Notice tone="danger">{grants.error.message}</Notice>
      ) : !grants.data ? (
        <Skeleton className="h-40 w-full" />
      ) : list.length === 0 ? (
        <EmptyState icon={LifeBuoy} title={t("console.support.none")}>
          {t("console.support.noneHint")}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <caption className="sr-only">{t("console.support.caption")}</caption>
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("console.support.tenant")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("supportAccess.reason")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("supportAccess.statusLabel")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("supportAccess.until")}
                </th>
                <th scope="col" className="px-4 py-2">
                  <span className="sr-only">{t("users.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((g) => (
                <tr key={g.id} className="align-top">
                  <th scope="row" className="px-4 py-2.5 text-left font-medium text-fg">
                    {g.tenantName}
                    {g.scope === "content" ? (
                      <span className="block text-xs font-normal text-fg-muted">
                        {t("console.support.withContent")}
                      </span>
                    ) : null}
                  </th>
                  <td className="px-4 py-2.5 break-words text-fg-secondary">{g.reason}</td>
                  <td className="px-4 py-2.5">
                    <GrantStatus status={g.status} />
                  </td>
                  <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                    {g.expiresAt
                      ? formatDateTime(g.expiresAt)
                      : t("supportAccess.hours", { count: g.requestedHours })}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {grantLive(g) ? (
                      <Button asChild size="sm" variant="outline">
                        <Link href={supportGrantPath(g.id)}>
                          {t("console.support.open", { tenant: g.tenantName })}
                        </Link>
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {requesting ? <RequestSupportDialog onClose={() => setRequesting(false)} /> : null}
    </Page>
  );
}

/** A request to one tenant: a reason (required) and up to 72 hours. */
export function RequestSupportDialog(props: { tenantId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const tenants = useTenants();
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState(props.tenantId ?? "");
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState("4");
  const n = Number(hours);
  const validHours = Number.isInteger(n) && n >= 1 && n <= MAX_HOURS;
  const request = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.operator.support.$post(
          { json: { tenantId, reason: reason.trim(), hours: n } },
          headers,
        ),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.support });
      props.onClose();
    },
  });
  const choices = (tenants.data ?? []).filter((x) => x.status !== "deleted");
  return (
    <AppDialog
      open
      title={t("console.support.requestTitle")}
      description={t("console.support.requestBody")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (tenantId && reason.trim() && validHours && !request.isPending) {
            request.mutate();
          }
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-tenant`}>{t("console.support.tenant")}</Label>
          <NativeSelect
            id={`${id}-tenant`}
            required
            value={tenantId}
            disabled={props.tenantId !== undefined}
            onChange={(e) => setTenantId(e.currentTarget.value)}
          >
            <option value="">{t("console.support.pickTenant")}</option>
            {choices.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-reason`}>{t("supportAccess.reason")}</Label>
          <Textarea
            id={`${id}-reason`}
            required
            maxLength={500}
            aria-describedby={`${id}-reason-hint`}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <span id={`${id}-reason-hint`} className="text-xs text-fg-muted">
            {t("console.support.reasonHint")}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-hours`}>{t("console.support.duration")}</Label>
          <Input
            id={`${id}-hours`}
            type="number"
            min={1}
            max={MAX_HOURS}
            step={1}
            required
            className="w-32"
            aria-invalid={!validHours}
            value={hours}
            onChange={(e) => setHours(e.currentTarget.value)}
          />
          <span className="text-xs text-fg-muted">{t("console.support.durationHint")}</span>
        </div>
        {request.error ? <Notice tone="danger">{consoleRefusal(request.error)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!tenantId || !reason.trim() || !validHours || request.isPending}
          >
            {t("console.support.send")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

/**
 * A tenant's backoffice through one of the operator's own grants: read-only, with a persistent
 * banner. Its own query cache, so nothing read under a grant ever shows elsewhere.
 */
export function SupportBackoffice(props: {
  grantId: string;
  section: BackofficeSection | null;
  projectId?: string | undefined;
  settingsGroup?: SettingsGroup | undefined;
}) {
  const { t } = useTranslation();
  const grant = useOwnGrant(props.grantId);
  const { csrfToken } = useOperator();
  const [client] = useState(
    () => new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 30_000 } } }),
  );
  // The tenant view's own client: its requests hold an automatic reload too.
  useEffect(() => watchQueryClient(client), [client]);
  if (grant.isError) {
    return (
      <Page width="narrow">
        <Notice tone="danger">{grant.error.message}</Notice>
      </Page>
    );
  }
  if (!grant.data) {
    return (
      <Page width="narrow">
        <Skeleton className="h-24 w-full" />
      </Page>
    );
  }
  const live = grantLive(grant.data);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <section
        aria-label={t("console.support.bannerLabel")}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-warning-border/50 bg-warning-bg px-4 py-2.5 text-sm text-warning-fg sm:px-6 md:px-10"
      >
        <ShieldAlert aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 font-medium">
          {live && grant.data.expiresAt
            ? t("console.support.banner", {
                tenant: grant.data.tenantName,
                until: formatDateTime(grant.data.expiresAt),
              })
            : t("console.support.bannerEnded", { tenant: grant.data.tenantName })}
        </span>
        <Link
          href={CONSOLE_SUPPORT_PATH}
          className="font-medium underline underline-offset-4 hover:no-underline"
        >
          {t("console.support.leave")}
        </Link>
      </section>
      {live ? (
        <QueryClientProvider client={client}>
          <SupportAdminApiProvider grantId={props.grantId} base={supportGrantPath(props.grantId)}>
            {/* Screens ask for a session: the operator's CSRF token, read as a tenant admin. */}
            <SessionProvider
              value={{ csrfToken, userId: "", entityId: "", roles: ["tenant_admin"] }}
            >
              <Backoffice
                section={props.section}
                projectId={props.projectId}
                settingsGroup={props.settingsGroup}
              />
            </SessionProvider>
          </SupportAdminApiProvider>
        </QueryClientProvider>
      ) : (
        <Page width="narrow">
          <Notice>{t("console.support.ended")}</Notice>
        </Page>
      )}
    </div>
  );
}
